import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { InstagramLogo, ArrowRight, Spinner } from "@phosphor-icons/react";
import { InstagramEmbed } from "react-social-media-embed";

const InstagramSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Instagram accounts
  const instagramAccounts = [
    {
      handle: "@jsblondsalon",
      username: "jsblondsalon",
      url: "https://www.instagram.com/jsblondsalon",
      label: "Salon",
      description: "Premium Blond Expertise & Salon Life",
    },
    {
      handle: "@jennifervallentin",
      username: "jennifervallentin",
      url: "https://www.instagram.com/jennifervallentin",
      label: "Jennifer",
      description: "Friseurmeisterin & Blond Expertin",
    },
  ];

  useEffect(() => {
    // Simulate loading time for embed
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [activeTab]);

  return (
    <section className="py-16 md:py-24 bg-[#F9F7F2]" data-testid="instagram-section">
      <div className="container-padding">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <InstagramLogo size={32} className="text-[#9A8C73]" weight="light" />
            <span className="caption">Folge uns auf Instagram</span>
          </div>
          
          <h2
            className="text-3xl md:text-4xl font-normal mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Live aus dem Salon
          </h2>
          <p className="text-[#2C2C2C]/60 max-w-xl mx-auto">
            Entdecke unsere neuesten Arbeiten, Behind-the-Scenes und Inspirationen direkt von Instagram
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10"
        >
          {instagramAccounts.map((account, index) => (
            <button
              key={account.handle}
              onClick={() => {
                setActiveTab(index);
                setIsLoading(true);
              }}
              data-testid={`instagram-tab-${index}`}
              className={`group relative px-6 py-4 rounded-full transition-all duration-300 ${
                activeTab === index
                  ? "bg-[#2C2C2C] text-white"
                  : "bg-white text-[#2C2C2C] hover:bg-[#2C2C2C]/5 border border-[#E6E2D6]"
              }`}
            >
              <div className="flex items-center gap-3">
                <InstagramLogo 
                  size={20} 
                  weight={activeTab === index ? "fill" : "regular"}
                  className={activeTab === index ? "text-white" : "text-[#9A8C73]"}
                />
                <div className="text-left">
                  <span className="font-medium block">{account.handle}</span>
                  <span className={`text-xs ${activeTab === index ? "text-white/60" : "text-[#2C2C2C]/50"}`}>
                    {account.label}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </motion.div>

        {/* Live Instagram Feed */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          {/* Account Info Card */}
          <div className="bg-white border border-[#E6E2D6] rounded-2xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#9A8C73] rounded-full flex items-center justify-center">
                  <InstagramLogo size={32} className="text-white" weight="fill" />
                </div>
                <div>
                  <h3 className="text-xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {instagramAccounts[activeTab].handle}
                  </h3>
                  <p className="text-[#2C2C2C]/60 text-sm">
                    {instagramAccounts[activeTab].description}
                  </p>
                </div>
              </div>
              <a
                href={instagramAccounts[activeTab].url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-follow-btn"
                className="btn-primary inline-flex items-center gap-2"
              >
                <InstagramLogo size={18} weight="fill" />
                Folgen
              </a>
            </div>
          </div>

          {/* Embed Container */}
          <div 
            className="bg-white rounded-2xl border border-[#E6E2D6] p-4 md:p-8 min-h-[500px] flex items-center justify-center"
            data-testid="instagram-embed-container"
          >
            {isLoading ? (
              <div className="flex flex-col items-center gap-4 text-[#9A8C73]">
                <Spinner size={40} className="animate-spin" />
                <p className="text-sm">Instagram Feed wird geladen...</p>
              </div>
            ) : (
              <div className="w-full flex justify-center">
                <InstagramEmbed
                  url={instagramAccounts[activeTab].url}
                  width={328}
                  captioned
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* Direct Links to Both Accounts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10"
        >
          {instagramAccounts.map((account, index) => (
            <motion.a
              key={account.handle}
              href={account.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`instagram-direct-link-${index}`}
              className="inline-flex items-center gap-2 text-[#2C2C2C]/70 hover:text-[#9A8C73] transition-colors group"
              whileHover={{ x: 5 }}
            >
              <InstagramLogo size={18} weight="regular" />
              <span>{account.handle}</span>
              <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.a>
          ))}
        </motion.div>

        {/* Note about Live Feed */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center text-xs text-[#2C2C2C]/40 mt-8"
        >
          Live Instagram Feed - Aktualisiert automatisch mit neuen Posts
        </motion.p>
      </div>
    </section>
  );
};

export default InstagramSection;
